import "./AdminSubscriptionsPage.scss";
import "../Home/HomePage.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header/Header";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { SubscriptionTypesApi } from "../../services/api/subscriptionTypes";
import { SubscriptionTypeResponse, SubscriptionTypeUpsertRequest } from "../../types/types";
import { isAdminRole } from "../../utils/roles";

const EMPTY_FORM: SubscriptionTypeUpsertRequest = {
  code: "",
  name: "",
  screenshotNum: 0,
  queryNum: 0,
};

const toDisplayValue = (value: string | null | undefined) => value ?? "-";

export const AdminSubscriptionsPage = () => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionTypeResponse[]>([]);
  const [createForm, setCreateForm] = useState<SubscriptionTypeUpsertRequest>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingForm, setEditingForm] = useState<SubscriptionTypeUpsertRequest>(EMPTY_FORM);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canAccess = useMemo(() => isAdminRole(user?.role), [user?.role]);

  const fetchSubscriptionTypes = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await SubscriptionTypesApi.getAll();
      setSubscriptionTypes(response);
    } catch (requestError) {
      const message =
        requestError instanceof Error ? requestError.message : "Не удалось загрузить подписки. Попробуйте еще раз.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) {
      return;
    }

    void fetchSubscriptionTypes();
  }, [canAccess, fetchSubscriptionTypes]);

  const resetCreateForm = useCallback(() => setCreateForm(EMPTY_FORM), []);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    resetCreateForm();
  }, [resetCreateForm]);

  const handleCreate = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setSaving(true);
      setError(null);

      try {
        const created = await SubscriptionTypesApi.create(createForm);
        setSubscriptionTypes((current) => [...current, created]);
        closeCreateModal();
      } catch (requestError) {
        const message = requestError instanceof Error ? requestError.message : "Не удалось создать подписку.";
        setError(message);
      } finally {
        setSaving(false);
      }
    },
    [closeCreateModal, createForm]
  );

  const startEdit = useCallback((item: SubscriptionTypeResponse) => {
    setEditingId(item.id);
    setEditingForm({
      code: item.code,
      name: item.name,
      screenshotNum: item.screenshotNum,
      queryNum: item.queryNum,
    });
    setError(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingForm(EMPTY_FORM);
  }, []);

  const handleUpdate = useCallback(
    async (id: number) => {
      setSaving(true);
      setError(null);

      try {
        const updated = await SubscriptionTypesApi.update(id, editingForm);
        setSubscriptionTypes((current) => current.map((item) => (item.id === id ? updated : item)));
        cancelEdit();
      } catch (requestError) {
        const message = requestError instanceof Error ? requestError.message : "Не удалось обновить подписку.";
        setError(message);
      } finally {
        setSaving(false);
      }
    },
    [cancelEdit, editingForm]
  );

  if (!canAccess) {
    return (
      <>
        <Header />
        <main className="admin-subscriptions">
          <section className="admin-subscriptions__card">
            <p className="admin-subscriptions__empty">Доступ к этому разделу есть только у администратора.</p>
          </section>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="admin-subscriptions">
        <section className="admin-subscriptions__hero">
          <div>
            <button className="admin-subscriptions__back" type="button" onClick={() => navigate("/app/admin")}>
              ← Назад
            </button>
            <p className="admin-subscriptions__eyebrow">Админка</p>
            <h1 className="admin-subscriptions__title">Подписки</h1>
            <p className="admin-subscriptions__subtitle">
              Управление типами подписок: создание новых тарифов и редактирование существующих.
            </p>
          </div>
          <aside className="admin-subscriptions__summary">
            <div>
              <strong>Всего:</strong> {subscriptionTypes.length}
            </div>
          </aside>
        </section>

        <section className="admin-subscriptions__card">
          <div className="admin-subscriptions__toolbar">
            <button
              className="admin-subscriptions__button admin-subscriptions__buttonPrimary"
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
            >
              Добавить подписку
            </button>
          </div>
          {error ? <p className="admin-subscriptions__error">{error}</p> : null}
        </section>

        <section className="admin-subscriptions__card admin-subscriptions__card_table">
          <div className="admin-subscriptions__tableWrap">
            <table className="admin-subscriptions__table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Screenshot num</th>
                  <th>Query num</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptionTypes.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <p className="admin-subscriptions__empty">{loading ? "Загрузка..." : "Подписки не найдены."}</p>
                    </td>
                  </tr>
                ) : (
                  subscriptionTypes.map((item) => {
                    const isEditing = editingId === item.id;
                    return (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>
                          {isEditing ? (
                            <input
                              className="admin-subscriptions__input"
                              value={editingForm.code}
                              onChange={(event) => setEditingForm((current) => ({ ...current, code: event.target.value }))}
                            />
                          ) : item.code}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              className="admin-subscriptions__input"
                              value={editingForm.name}
                              onChange={(event) => setEditingForm((current) => ({ ...current, name: event.target.value }))}
                            />
                          ) : item.name}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              className="admin-subscriptions__input"
                              type="number"
                              min={0}
                              value={editingForm.screenshotNum}
                              onChange={(event) =>
                                setEditingForm((current) => ({ ...current, screenshotNum: Number(event.target.value) }))
                              }
                            />
                          ) : item.screenshotNum}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              className="admin-subscriptions__input"
                              type="number"
                              min={0}
                              value={editingForm.queryNum}
                              onChange={(event) =>
                                setEditingForm((current) => ({ ...current, queryNum: Number(event.target.value) }))
                              }
                            />
                          ) : item.queryNum}
                        </td>
                        <td>{toDisplayValue(item.createdAt)}</td>
                        <td>{toDisplayValue(item.updatedAt)}</td>
                        <td>
                          <div className="admin-subscriptions__actions">
                            {isEditing ? (
                              <>
                                <button
                                  className="admin-subscriptions__button admin-subscriptions__buttonPrimary"
                                  type="button"
                                  onClick={() => void handleUpdate(item.id)}
                                  disabled={saving}
                                >
                                  Сохранить
                                </button>
                                <button className="admin-subscriptions__button" type="button" onClick={cancelEdit} disabled={saving}>
                                  Отмена
                                </button>
                              </>
                            ) : (
                              <button className="admin-subscriptions__button" type="button" onClick={() => startEdit(item)} disabled={saving}>
                                Изменить
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {isCreateModalOpen ? (
        <section className="home-modal">
          <div className="home-modal__backdrop" onClick={closeCreateModal} />
          <div className="home-modal__dialog">
            <button className="home-modal__close" type="button" onClick={closeCreateModal}>x</button>
            <h2 className="home-modal__title">Новая подписка</h2>
            <form className="home-modal__form" onSubmit={handleCreate}>
              <input
                className="home-modal__input"
                type="text"
                placeholder="Code"
                value={createForm.code}
                onChange={(event) => setCreateForm((current) => ({ ...current, code: event.target.value }))}
              />
              <input
                className="home-modal__input"
                type="text"
                placeholder="Название"
                value={createForm.name}
                onChange={(event) => setCreateForm((current) => ({ ...current, name: event.target.value }))}
              />
              <input
                className="home-modal__input"
                type="number"
                min={0}
                placeholder="Количество скриншотов"
                value={createForm.screenshotNum}
                onChange={(event) => setCreateForm((current) => ({ ...current, screenshotNum: Number(event.target.value) }))}
              />
              <input
                className="home-modal__input"
                type="number"
                min={0}
                placeholder="Количество запросов"
                value={createForm.queryNum}
                onChange={(event) => setCreateForm((current) => ({ ...current, queryNum: Number(event.target.value) }))}
              />
              <button className="home-modal__submit" type="submit" disabled={saving}>
                {saving ? "Сохранение..." : "Создать"}
              </button>
            </form>
          </div>
        </section>
      ) : null}
    </>
  );
};
